import { BackendURLContantas } from "@/constants/URLConstants";
import axios, { AxiosError, AxiosResponse } from "axios";
import { ContestFindRequest } from "@/hooks/contest/useContestFind";
import { ContestStoreRequest } from "@/hooks/contest/useContestStore";
import { ContestUpdateRequest } from "@/hooks/contest/useContestUpdate";
import { ContestCatetoryResponseData } from "./category";
import { GroupIndexResponse, GroupsIndexRequest } from "../group";

// ロックフィルター
export type LockFilter = "all" | "locked" | "unlocked";

// Contest 一覧レスポンス型
export type ContestResponse = {
  data: ContestResponseData[];
  currentPage: number;
  lastPage: number;
  total: number;
  firstPageUrl: string;
  lastPageUrl: string;
  nextPageUrl: string | null;
  prevPageUrl: string | null;
  from: number;
  to: number;
  perPage: number;
  path: string;
  links: PaginationLink[];
};

export type ContestUsersResponse = {
  data: Array<{
    uuid: string;
    name: string | null;
    userId: string | null;
    userIconFile?: {
      id?: number;
      userUuid?: string;
      fileUrl?: string;
      fileName?: string;
    } | null;
  }>;
  currentPage: number;
  lastPage: number;
  total: number;
  perPage?: number;
  nextPageUrl?: string | null;
  prevPageUrl?: string | null;
};

export type ContestUsersRequest = {
  page?: number;
  perPage?: number;
  q?: string;
};

type ContestOrganizer = {
  userId: string;
  uuid: string;
};

export type ContestResponseData = {
  id: number;
  title: string | null;
  description: string | null;
  bannerImageUrl: string | null;
  bannerImageName: string | null;
  startDate: string | null;
  scoringStartDate: string | null;
  contestEndDate: string | null;
  isPublished: boolean | number;
  isLocked: boolean;
  lockedBy?: string | null;
  lockedAt?: string | null;
  lockReason?: string | null;
  createdAt: string;
  updatedAt: string;
  group: GroupData;
  categories: ContestCatetoryResponseData[];
  organizer?: ContestOrganizer;
  categoryIds: number[];
  status?: string;
  role?: "ADMIN" | "USER";
};

export type ContestCreateResponseData = { id: number };

export type GroupData = {
  id: number;
  name: string;
  url1: string | null;
  url2: string | null;
  ownerUuid: string;
  iconFileUrl: string | null;
  iconName: string | null;
  groupType: number;
  contestType?: number | null;
  desc: string | null;
  createdAt: string;
  updatedAt: string;
  pivot?: {
    contestId: number;
    groupId: number;
    createdAt: string;
    updatedAt: string;
  };
};

export type PaginationLink = {
  url: string | null;
  label: string;
  active: boolean;
};

export type ContestScreenType = "settings" | "public";

export type ContestSearchRequest = {
  page?: number;
  perPage?: number;
  isPublished?: boolean;
  isLocked?: boolean;
  q?: string;
  groupId?: number;
  statuses?: string[];
  categoryIds?: number[];
  userIds?: string[];
  screen: ContestScreenType;
  startFrom?: string; // yyyy-mm-dd
  startTo?: string; // yyyy-mm-dd
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  viewer?: "admin" | "user";
  actorUuid?: string;
};

export type ContestBulkPublishRequest = {
  organizerUuid: string;
  isPublished: boolean; // true: 公開, false: 非公開
};

export type ContestBulkDeleteRequest = {
  organizerUuid: string;
};

export type ContestBulkActionResponse = {
  message: string; // "success"
  organizerUuid: string;
  affected: number;
  isPublished?: boolean;
};

export type BulkConfirmAction =
  | "publish"
  | "unpublish"
  | "delete"
  | "lock"
  | "unlock"
  | null;

export type ContestBulkActionByIdsRequest = {
  contestIds: number[];
  action: BulkConfirmAction;
  actorUuid: string;
  lockReason?: string;
};

export type ContestBulkActionByIdsResponse = {
  message: string;
  affected: number;
  action: BulkConfirmAction;
  ids: number[];
};

const appendArray = (
  params: URLSearchParams,
  key: string,
  values?: Array<string | number>
) => {
  if (!values || values.length === 0) return;
  values.forEach((v) => params.append(`${key}[]`, String(v)));
};

// コンテストAPI
export const ContestAPI = {
  /**
   * コンテスト一覧取得（ページネーション＋フィルタ対応）
   */
  search: async (req: ContestSearchRequest): Promise<ContestResponse> => {
    const page = req.page ?? 1;
    const perPage = req.perPage ?? 10;

    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("per_page", String(perPage));
    params.set("screen", req.screen);

    if (req.viewer) params.set("viewer", req.viewer);
    if (req.startFrom) params.set("start_from", req.startFrom);
    if (req.startTo) params.set("start_to", req.startTo);
    if (req.isPublished !== undefined) params.set("is_published", String(req.isPublished));
    if (req.isLocked !== undefined) params.set("is_locked", String(req.isLocked));
    if (req.q?.trim()) params.set("q", req.q.trim());
    if (req.groupId !== undefined) params.set("group_id", String(req.groupId));
    if (req.sortBy) params.set("sort_by", req.sortBy);
    if (req.sortOrder) params.set("sort_order", req.sortOrder);

    const actor = req.actorUuid?.trim();
    if (actor) params.set("actor_uuid", actor);

    appendArray(
      params,
      "status",
      req.statuses?.slice().filter(Boolean)
    );

    appendArray(
      params,
      "category_ids",
      req.categoryIds?.slice().filter((n) => Number.isFinite(Number(n)))
    );

    appendArray(
      params,
      "user_ids",
      req.userIds?.slice().map((s) => s.trim()).filter(Boolean)
    );

    const url = `${BackendURLContantas.CONTEST.SEARCH.PATH_NAME}?${params.toString()}`;
    const { data } = await axios.get<unknown, AxiosResponse<ContestResponse>>(url);

    return data;
  },

  find: async (payload: ContestFindRequest): Promise<ContestResponseData> => {
    try {
      const { data } = await axios.post<
        ContestFindRequest,
        AxiosResponse<ContestResponseData>
      >(`${BackendURLContantas.CONTEST.FIND.PATH_NAME}`, payload);

      return data;
    } catch (error: unknown) {
      if (typeof window !== "undefined") {
        window.location.href = "/";
      }
      throw new AxiosError();
    }
  },

  getContestUsers: async (req: ContestUsersRequest = {}): Promise<ContestUsersResponse> => {
    const page = req.page ?? 1;
    const perPage = req.perPage ?? 20;

    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("per_page", String(perPage));

    if (req.q && req.q.trim()) {
      params.set("q", req.q.trim());
    }

    const url = `${BackendURLContantas.CONTEST.USERS.PATH_NAME}?${params.toString()}`;
    const { data } = await axios.get<unknown, AxiosResponse<ContestUsersResponse>>(url);

    return data;
  },

  /**
   * コンテスト作成
   */
  post: async (payload: ContestStoreRequest): Promise<ContestCreateResponseData> => {
    const { data } = await axios.post<
      ContestStoreRequest,
      AxiosResponse<ContestCreateResponseData>
    >(`${BackendURLContantas.CONTEST.POST.PATH_NAME}`, payload);

    return data;
  },

  /**
   * コンテスト更新
   */
  update: async (payload: ContestUpdateRequest): Promise<ContestResponseData> => {
    const { data } = await axios.post<
      ContestUpdateRequest,
      AxiosResponse<ContestResponseData>
    >(`${BackendURLContantas.CONTEST.UPDATE.PATH_NAME}`, payload);

    return data;
  },

  /**
   * コンテスト削除
   */
  delete: async (payload: ContestFindRequest): Promise<ContestResponseData> => {
    const { data } = await axios.post<
      ContestFindRequest,
      AxiosResponse<ContestResponseData>
    >(`${BackendURLContantas.CONTEST.DELETE.PATH_NAME}`, payload);

    return data;
  },

  getMyGroups: async (request: GroupsIndexRequest, page: number = 1) => {
    const { data } = await axios.post<
      GroupsIndexRequest,
      AxiosResponse<GroupIndexResponse>
    >(`${BackendURLContantas.CONTEST.GROUP.PATH_NAME}?page=${page}`, request);

    return data;
  },

  /**
   * 一括：開催者のコンテストを公開/非公開
   */
  bulkPublishByOrganizer: async (
    payload: ContestBulkPublishRequest
  ): Promise<ContestBulkActionResponse> => {
    const { data } = await axios.post<
      ContestBulkPublishRequest,
      AxiosResponse<ContestBulkActionResponse>
    >(BackendURLContantas.CONTEST.BULK.PUBLISH_BY_ORGANIZER.PATH_NAME, payload);

    return data;
  },

  /**
   * 一括：開催者のコンテストを削除
   */
  bulkDeleteByOrganizer: async (
    payload: ContestBulkDeleteRequest
  ): Promise<ContestBulkActionResponse> => {
    const { data } = await axios.post<
      ContestBulkDeleteRequest,
      AxiosResponse<ContestBulkActionResponse>
    >(BackendURLContantas.CONTEST.BULK.DELETE_BY_ORGANIZER.PATH_NAME, payload);

    return data;
  },

  /**
   * 複数選択一括操作
   */
  bulkActionByIds: async (
    payload: ContestBulkActionByIdsRequest
  ): Promise<ContestBulkActionByIdsResponse> => {
    const { data } = await axios.post<
      ContestBulkActionByIdsRequest,
      AxiosResponse<ContestBulkActionByIdsResponse>
    >(BackendURLContantas.CONTEST.BULK.ACTION.PATH_NAME, payload);

    return data;
  },
};